import { useTranslation } from "react-i18next";
import type { Article } from "@/mocks/articles";

interface ArticleKeyPointsProps {
  article: Article;
}

export default function ArticleKeyPoints({ article }: ArticleKeyPointsProps) {
  const { t } = useTranslation();
  const hasKeyPoints = article.keyPoints && article.keyPoints.length > 0;
  const hasImpact    = article.impact    && article.impact.length    > 0;
  const hasQuote     = article.quote;

  if (!hasKeyPoints && !hasImpact && !hasQuote) return null;

  return (
    <div className="space-y-6 mb-8 md:mb-10">

      {/* Impact en chiffres */}
      {hasImpact && (
        <div className="bg-gray-900 rounded-2xl p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-500/20">
              <i className="ri-bar-chart-2-line text-orange-400 text-base"></i>
            </div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider">{t("keypoints.impact")}</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            {article.impact!.map((item) => (
              <div
                key={item.label}
                className="bg-white/5 border border-white/10 rounded-xl p-3 md:p-4 text-center hover:bg-white/10 transition-colors"
              >
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 rounded-lg bg-orange-500/15">
                  <i className={`${item.icon} text-orange-400 text-base`}></i>
                </div>
                <div className="text-white font-bold text-lg md:text-xl leading-none mb-1">{item.value}</div>
                <div className="text-gray-400 text-xs leading-tight">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Points clés */}
      {hasKeyPoints && (
        <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-500">
              <i className="ri-list-check-2 text-white text-base"></i>
            </div>
            <h3 className="text-gray-900 font-bold text-sm uppercase tracking-wider">{t("keypoints.keypoints")}</h3>
          </div>
          <ul className="space-y-3">
            {article.keyPoints!.map((point, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold mt-0.5">
                  {i + 1}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{point}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Citation */}
      {hasQuote && (
        <div className="relative bg-white border border-gray-100 rounded-2xl p-5 md:p-6 overflow-hidden">
          <div className="absolute top-3 right-4 text-orange-100 text-8xl font-serif leading-none select-none pointer-events-none" aria-hidden="true">
            &ldquo;
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50">
                <i className="ri-double-quotes-l text-orange-500 text-base"></i>
              </div>
              <h3 className="text-gray-500 font-semibold text-xs uppercase tracking-wider">{t("keypoints.quote")}</h3>
            </div>
            <blockquote className="text-gray-800 text-base md:text-lg font-serif italic leading-relaxed mb-3">
              &ldquo;{article.quote}&rdquo;
            </blockquote>
            {article.quoteAuthor && (
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 rounded-full bg-orange-400"></div>
                <cite className="text-orange-600 text-sm font-semibold not-italic">{article.quoteAuthor}</cite>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
